/** Tenant-admin Access screen — per-member capability grid.
 *
 * THE contract this page relies on (pinned in backend tests): every
 * grant made here IS enforced by the API. The grid shows effective
 * capabilities (role ∩ modules ± overrides) straight from the same
 * resolver the enforcement uses, so UI and API can never disagree.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../api/http";
import * as api from "../../api/team";
import type { Capabilities, Override, TeamMember } from "../../api/types";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, EmptyState, Select, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

/** Domains an admin can grant on this screen. `profit` IS listed —
 * grantable, but only ever explicitly (it's wildcard-excluded server-side). */
export const GRANTABLE_DOMAINS = [
  "learning",
  "people",
  "finance",
  "comms",
  "analytics",
  "profit",
] as const;

type CellState = "granted" | "revoked" | "default";

export function cellState(overrides: Override[], domain: string, action: "read" | "write"): CellState {
  const override = overrides.find((o) => o.domain === domain && o.action === action);
  if (!override) return "default";
  return override.granted ? "granted" : "revoked";
}

export function AccessPage() {
  const t = useT();
  const { push } = useToast();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [caps, setCaps] = useState<Capabilities | null>(null);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [loadingGrid, setLoadingGrid] = useState(false);

  useEffect(() => {
    api
      .listTeam()
      .then((rows) => {
        // Admins have everything by definition; the grid is for the rest.
        const grantable = rows.filter((r) => r.role !== "tenant_admin");
        setMembers(grantable);
        if (grantable.length > 0) setSelectedId(grantable[0].id);
      })
      .catch(() => setMembers([]));
  }, []);

  const loadGrid = useCallback(async (memberId: string) => {
    setLoadingGrid(true);
    try {
      const [c, o] = await Promise.all([
        api.memberCapabilities(memberId),
        api.listOverrides(memberId),
      ]);
      setCaps(c);
      setOverrides(o);
    } finally {
      setLoadingGrid(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) void loadGrid(selectedId);
  }, [selectedId, loadGrid]);

  const selected = useMemo(() => members.find((m) => m.id === selectedId), [members, selectedId]);

  /** Cycle default → granted → revoked → default. */
  const onCycle = async (domain: string, action: "read" | "write") => {
    const state = cellState(overrides, domain, action);
    try {
      if (state === "default") {
        await api.setOverride(selectedId, { domain, action, granted: true });
      } else if (state === "granted") {
        await api.setOverride(selectedId, { domain, action, granted: false });
      } else {
        await api.clearOverride(selectedId, domain, action);
      }
      await loadGrid(selectedId);
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    }
  };

  const STATE_TONE: Record<CellState, "success" | "danger" | "neutral"> = {
    granted: "success",
    revoked: "danger",
    default: "neutral",
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold">{t("access.title")}</h1>
        <p className="mt-1 text-muted">{t("access.subtitle")}</p>
      </div>

      {members.length === 0 ? (
        <Card>
          <EmptyState message="No members to manage yet." />
        </Card>
      ) : (
        <Card>
          <div className="mb-4 w-72">
            <Select
              aria-label="member"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name} — {m.role}
                </option>
              ))}
            </Select>
          </div>

          {loadingGrid || !caps ? (
            <div className="grid place-items-center py-8">
              <Spinner size={28} />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase text-muted">
                  <th className="pb-2">{t("access.domain")}</th>
                  <th className="pb-2">{t("access.read")}</th>
                  <th className="pb-2">{t("access.write")}</th>
                </tr>
              </thead>
              <tbody>
                {GRANTABLE_DOMAINS.map((domain) => (
                  <tr key={domain} className="border-b border-line/60 last:border-0">
                    <td className="py-3 font-semibold capitalize text-ink">{domain}</td>
                    {(["read", "write"] as const).map((action) => {
                      const state = cellState(overrides, domain, action);
                      const effective = caps[action].includes(domain);
                      return (
                        <td key={action} className="py-3">
                          <Button
                            variant="ghost"
                            aria-label={`${domain} ${action}: ${state}, effective ${effective ? "yes" : "no"}`}
                            onClick={() => onCycle(domain, action)}
                          >
                            <Badge tone={STATE_TONE[state]}>
                              {effective ? "✓" : "✕"} {t(`access.${state === "default" ? "default" : state}`)}
                            </Badge>
                          </Button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selected && (
            <p className="mt-3 text-xs text-muted">
              {selected.full_name}: {t("access.default")} = {selected.role}
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
